import React, { useState } from "react";
import { useDispatch } from "react-redux";
import MaterialModal from "../MaterialModal";
import {
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputLabel,
    Select,
    MenuItem,
} from "@material-ui/core";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import Moment from "moment";
import MomentUtils from "@date-io/moment";
import { parseDate } from "../../../helpers/parseDate";
import withModal from "../withModal";
import { hideModal } from "../../../actions/actionCreators";
import { getUserCompanyCompletedFlowsStatus } from "../../../actions/dashboardWidgetActions";

type Props = {
    isOpen: boolean;
};

const FlowStatusDateFilter: React.FC<Props> = ({ isOpen }: Props) => {
    const dispatch = useDispatch();

    // Setup default dates
    const date = new Date();
    const today = date.toJSON().slice(0, 10).replace(/-/g, "/");
    date.setMonth(date.getMonth() - 1);
    const monthAgo = date.toJSON().slice(0, 10).replace(/-/g, "/");

    // used and handled in the filter date modal
    // need to have here so that we can maintain the search state when refreshing
    const [filterBy, setFilterBy] = useState("daysAgo");
    const [daysAgo, setDaysAgo] = useState(30);
    const [year, setYear] = useState(2022);
    const [start, setStartDate] = useState(monthAgo);
    const [stop, setStopDate] = useState(today);

    const hide = () => dispatch(hideModal("FLOW_STATUS_DATE_FILTER"));

    // When a user clicks search, search using the information on the selected radio button
    const onSearch = () => {
        switch (filterBy) {
            case "daysAgo":
                dispatch(getUserCompanyCompletedFlowsStatus("", "", daysAgo, 0));
                break;
            case "year":
                dispatch(getUserCompanyCompletedFlowsStatus("", "", 30, year));
                break;
            case "range":
                dispatch(getUserCompanyCompletedFlowsStatus(start, stop, 30, 0));
                break;
            default:
                break;
        }
    };

    const searchClick = () => {
        onSearch();
        hide();
    };

    const getYears = () => {
        let year = new Date().getFullYear();
        let years: Array<number> = [];

        for (let i = 0; i < 5; i++) {
            years.push(year);
            year -= 1;
        }

        return years;
    };

    const setMomentDate = (type, momentObj) => {
        if (!momentObj || (momentObj && momentObj.utc().format() == "Invalid date")) {
            return false;
        }

        const d = parseDate(momentObj.utc().format()).toJSON().slice(0, 10).replace(/-/g, "/");
        if (type == "start") {
            setStartDate(d);
        } else if (type == "stop") {
            setStopDate(d);
        }
    };

    return (
        <MaterialModal
            id="flows-completed-date-search-modal"
            isOpen={isOpen}
            title="Flows Completed Date Filter"
            onClose={hide}
            cancelButtonText="Cancel"
            onPrimaryButtonRequest={searchClick}
            primaryButtonText="Search"
        >
            <p>
                Search for completed flows that have completion times within the parameters specified below. <br />
                You can search exclusively within a given number of months, by year, or between two dates.
            </p>
            <div className="col-md-5" style={{ minHeight: "150px" }}>
                <FormControl>
                    <FormLabel id="filter-completed-flows">Filter By:</FormLabel>
                    <RadioGroup
                        aria-labelledby="filter-completed-flows-label"
                        onChange={e => setFilterBy(e.target.value as string)}
                        value={filterBy}
                        name="filter-completed-flows-radio-buttons-group"
                    >
                        <FormControlLabel
                            value="daysAgo"
                            control={<Radio color="primary" />}
                            label="Last 1, 3, or 6 months"
                        />
                        <FormControlLabel value="year" control={<Radio color="primary" />} label="Year" />
                        <FormControlLabel value="range" control={<Radio color="primary" />} label="Date Range" />
                    </RadioGroup>
                </FormControl>
            </div>
            <div className="col-md-7" style={{ display: "flex", alignItems: "center", minHeight: "150px" }}>
                {filterBy == "daysAgo" && (
                    <div style={{ width: "375px" }}>
                        <FormControl fullWidth>
                            <InputLabel>Months Ago</InputLabel>
                            <Select
                                labelId="daysAgo-select-label"
                                id="daysAgo-select"
                                value={daysAgo}
                                label="Months Ago"
                                onChange={e => setDaysAgo(e.target.value as number)}
                            >
                                <MenuItem value={30}>1 Month</MenuItem>
                                <MenuItem value={90}>3 Months</MenuItem>
                                <MenuItem value={180}>6 Months</MenuItem>
                            </Select>
                        </FormControl>
                    </div>
                )}
                {filterBy == "year" && (
                    <div style={{ width: "375px" }}>
                        <FormControl fullWidth>
                            <InputLabel>Year</InputLabel>
                            <Select
                                labelId="year-select-label"
                                id="year-select"
                                value={year}
                                label="Year"
                                onChange={e => setYear(e.target.value as number)}
                            >
                                {getYears().map(year => (
                                    <MenuItem key={year.toString() + "-year"} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}
                {filterBy == "range" && (
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                        <div style={{ margin: 5 }}>
                            <KeyboardDatePicker
                                variant="inline"
                                format="MM/DD/YYYY"
                                margin="normal"
                                id="begin-date-range"
                                label="Begin Date"
                                value={Moment(start, "YYYY/MM/DD")}
                                onChange={momentObj => setMomentDate("start", momentObj)}
                                KeyboardButtonProps={{
                                    "aria-label": "change date",
                                }}
                                disableFuture
                            />
                        </div>
                        <div style={{ margin: 5 }}>
                            <KeyboardDatePicker
                                variant="inline"
                                format="MM/DD/YYYY"
                                margin="normal"
                                id="end-date-range"
                                label="End Date"
                                value={Moment(stop, "YYYY/MM/DD")}
                                onChange={momentObj => setMomentDate("stop", momentObj)}
                                KeyboardButtonProps={{
                                    "aria-label": "change date",
                                }}
                                disableFuture
                            />
                        </div>
                    </MuiPickersUtilsProvider>
                )}
            </div>
        </MaterialModal>
    );
};

export default withModal(FlowStatusDateFilter, "FLOW_STATUS_DATE_FILTER");
